/**
 * @description 管理VR模块，主要是在VR中生成各个UI面板
 * @author TKB
 * @creat 2023-03-24 15:38:24
 */
import {
  Object3D,
  Mesh,
  BufferGeometry,
  Raycaster,
  Vector3,
  Line,
  Matrix4,
  MeshBasicMaterial,
  Color,
  Box3,
  DoubleSide,
  LineBasicMaterial,
  Vector2,
  TorusGeometry,
  PlaneGeometry,
  SphereGeometry,
  Quaternion,
  Clock,
  Box3Helper,
} from "three";
import { VRButton } from "../lib/VRButton.js";
import { UnityVRButton } from "../lib/UnityVRButton.js";
import { UIHandRaySystem, ObjectHandRaySystem } from "../Escy/Systems.js";
import { World } from "ecsy";
import {
  Object3DCmp,
  UIIntersectableCmp,
  ObjectIntersectableCmp,
} from "../Escy/Components.js";
import { BaseManager } from "./BaseManager.js";
import Util from "../utils/utils.js";
import { VRHandleButtonControls } from "../utils/VRHandleButtonControls.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { MeshBVH, MeshBVHVisualizer } from "three-mesh-bvh";
import { EventManagerCommand } from "./EventManager.js";
import { VUILib } from "../VUILib/VUILib.js";
import VUtil from "../VUILib/VUtils.js";
import { MainPanel } from "../VRPanels/MainPanel.js";
import { SystemTipPanel } from "../VRPanels/SystemTipPanel.js";
import { ProgressBarPanel } from "../VRPanels/ProgressBarPanel.js";
import {
  HorizontalAlignment,
  VerticleAlignment,
  VGridLayout,
  VHorizontalLayout,
  VVerticleLayout,
} from "../VUILib/UILayout/VLayout";
import { ClickMode } from "../Interface3D.js";

const VRDeviceType = {
  Pico: "Pico",
  Pico4: "Pico4",
  PicoNeo3: "PicoNeo3",
  Quest: "Quest",
  Quest2: "Quest2",
  QuestPro: "QuestPro",
  HTCVive: "HTCVive",
  HTCViveFocus3: "HTCViveFocus3",
  HTCViveFocus: "HTCViveFocus",
  SteamVR: "SteamVR",
  UnknownVRDevice: "UnknownVRDevice",
  NotVRDevice: "NotVRDevice",
};

/**
 * 检测是否是VR设备并且是什么VR设备
 * @param {*} userAgent
 * @returns
 */
function detectVRDevice(userAgent) {
  userAgent = userAgent.toLowerCase();
  console.log("userAgent", userAgent);
  // 先检查Pico，因为pico浏览器中userAgent会出现Quest等字段
  if (userAgent.includes("pico")) {
    if (userAgent.includes("pico 4")) {
      return VRDeviceType.Pico4;
    } else if (userAgent.includes("pico neo 3")) {
      return VRDeviceType.PicoNeo3;
    } else {
      return VRDeviceType.Pico;
    }
  }
  if (userAgent.includes("quest")) {
    if (userAgent.includes("quest 2")) {
      return VRDeviceType.Quest2;
    } else if (userAgent.includes("quest pro")) {
      return VRDeviceType.QuestPro;
    } else {
      return VRDeviceType.Quest;
    }
  }
  if (userAgent.includes("vive")) {
    if (userAgent.includes("vive focus 3")) {
      return VRDeviceType.HTCViveFocus3;
    } else if (userAgent.includes("vive focus")) {
      return VRDeviceType.HTCViveFocus;
    } else {
      return VRDeviceType.HTCVive;
    }
  }
  if (userAgent.includes("steamvr")) {
    return VRDeviceType.SteamVR;
  }

  if (
    userAgent.includes("vr safari") ||
    userAgent.includes("oculusbrowser") ||
    userAgent.includes("vivebrowser") ||
    userAgent.includes("picobrowser")
  ) {
    return VRDeviceType.UnknownVRDevice;
  }
  return VRDeviceType.NotVRDevice;
}

export class VRManager extends BaseManager {
  constructor(manager) {
    super(manager);
  }

  async init(manager) {
    super.init(manager);
    this.sceneManager = manager.sceneManager;
    this.el = this.sceneManager.el;
    this.renderer = this.sceneManager.renderer;
    this.camera = this.sceneManager.defaultCamera;
    this.scene = this.sceneManager.scene; //this.sceneManager.cameraScene
    this.terrainPanel=null;

    this.isWindows = Util.getOS().isPc;

    this.deviceType = detectVRDevice(navigator.userAgent);

    this.isWindows = false; //!this.isWindows; // 用于在PC上使用谷歌插件模拟VR环境

    this.isInVR = false; // 是否在VR模式
    this.mainController = null;

    this.clock = new Clock();

    this.controller1 = null;
    this.controller2 = null;
    this.moveSpeed = 3; // 手柄方向键移动速度
    this.rotateSpeed = 1; // 旋转相机速度
    this.scaleModelSpeed = 1; // 手柄抓取到模型后使用方向键缩放速度
    this.rotateModelSpeed = 1; // 手柄抓取到模型后使用方向键旋转速度
    this.handleGrabModelCount = 0; // 当前手柄抓取的物体数量，可以有两个手柄，分别抓取
    this.renderer.xr.enabled = true;
    //this.renderer.xr.setFramebufferScaleFactor(2.0) //增加Quest分辨率

    this._initEcsyLib();

    this.showBtn = false; // 是否显示VR按钮 加载完所有模型等待1秒再显示

    this.uiLib = new VUILib(
      this.camera,
      this.world,
      this.scene,
      this.deviceType !== VRDeviceType.NotVRDevice
    );

    this.loadHandleModel(() => {
      let waitInit = setInterval(() => {
        if (this.showBtn && this.world) {
          clearInterval(waitInit);

          this.vrBtn = VRButton.createButton(
            this.renderer,
            this._setup.bind(this),
            this._setInputSourceList.bind(this),
            this._setXrSession.bind(this)
          );
          this.el.appendChild(this.vrBtn);

          //this.unityvrBtn=UnityVRButton.createButton(this.manager);
          //this.el.appendChild(this.unityvrBtn);
        }
      }, 500);
    });

    this.tempMatrix = new Matrix4();
    this.raycaster = new Raycaster();

    // 用于滑动控制
    this.isControlSlider = false;
  }

  /**
   * 初始化ecsy插件
   */
  _initEcsyLib() {
    this.world = new World();
    this.world.registerComponent(Object3DCmp);
    this.world.registerComponent(UIIntersectableCmp);
    this.world.registerComponent(ObjectIntersectableCmp);
  }

  async _setXrSession(session) {
    this.xrSession = session;

    // 创建基于地面的参考系
    this.floorfSpace = await session.requestReferenceSpace("local-floor");
  }

  /**
   * 进入VR时用于获取头显高度
   * @param {*} xrFrame
   */
  setVRCameraHeight(xrFrame) {
    // 创建基于地面的参考系
    if (xrFrame && this.floorfSpace && !this.initCameraHeight) {
      const pose = xrFrame.getViewerPose(this.floorfSpace);
      this.initCameraHeight = pose.transform.position.y;
      this.camera.position.y = this.initCameraHeight;
    }
  }

  /**
   * 加载手柄模型
   */
  async loadHandleModel(cb) {
    this.handleModels = {
      pico4Right: (
        await this.manager.loadManager.loadScene(
          "/assets/handle/picoRight.zip" //"/assets/handle/pico4Right.zip"
        )
      ).scene,
      pico4Left: (
        await this.manager.loadManager.loadScene(
          "/assets/handle/picoLeft.zip" /*"/assets/handle/pico4Left.zip"*/
        )
      ).scene,
      picoRight: (
        await this.manager.loadManager.loadScene("/assets/handle/picoRight.zip")
      ).scene,
      picoLeft: (
        await this.manager.loadManager.loadScene("/assets/handle/picoLeft.zip")
      ).scene,
      htc: (await this.manager.loadManager.loadScene("/assets/handle/htc.zip"))
        .scene,
    };

    if (cb) cb();
  }

  /**
   * 检测到手柄接入时的回调，进入vr时会调用一次
   * @param {*} list
   */
  async _setInputSourceList(list) {
    this.vrInputSource = list;
    console.log(list);
    /******添加VR手柄*******/
    if (
      !this.controller1 &&
      this.vrInputSource.length >= 1 &&
      this.vrInputSource[0].profiles.length > 0
    ) {
      this.controller1 = this.renderer.xr.getController(0);

      this.VRCamera.add(this.controller1);
      this.vrInputSource[0]._target = { target: this.controller1 };

      this.controllerGrip1 = this.renderer.xr.getControllerGrip(0);
      let handModel;
      // pico
      if (!this.isWindows) {
        // 加载pico右手柄
        if (this.vrInputSource[0].handedness === "right") {
          console.log("创建右手柄");
          if (this.deviceType === VRDeviceType.Pico4)
            handModel = this.handleModels.pico4Right;
          else handModel = this.handleModels.picoRight;
        } else {
          console.log("创建左手柄");
          if (this.deviceType === VRDeviceType.Pico4)
            handModel = this.handleModels.pico4Left;
          else handModel = this.handleModels.picoLeft;
        }
      } else {
        console.log("创建手柄1");
        handModel = this.handleModels.htc.clone();
      }
      this.controller1.inputSource = this.vrInputSource[0];

      this.controller1._handModel = handModel;
      if (!this.isWindows) {
        handModel.rotateX(-Math.PI / 4);
      }
      this.controllerGrip1.add(handModel);
      this.VRCamera.add(this.controllerGrip1);

      if (this.vrInputSource[0].handedness === "right") {
        this.rightController = this.controller1;
      } else {
        this.leftController = this.controller1;
      }

      let type = "htc";
      if (this.deviceType === VRDeviceType.Pico4) type = "pico4";
      else if (this.deviceType === VRDeviceType.PicoNeo3) type = "pico3";
      this.controller1._handdleButtonControl = new VRHandleButtonControls(
        handModel,
        type,
        this.vrInputSource[0].handedness,
        this
      );

      //line可视
      let line_geometry = new BufferGeometry().setFromPoints([
        new Vector3(0, 0, 0),
        new Vector3(0, 0, -1),
      ]);
      let line = new Line(
        line_geometry,
        new LineBasicMaterial({
          depthWrite: false,
          depthTest: false,
          transparent: true,
        })
      );
      line.scale.z = 1.5;
      line.renderOrder = Infinity;
      line.name = "line";
      this.controller1.add(line);
      this.controller1._line = line;

      // 优先右手柄作为主控制器
      if (
        this.vrInputSource.length === 1 ||
        this.vrInputSource[0].handedness === "right"
      ) {
        this.mainController = this.controller1;
        this.controller1._isMain = true;
        line.material.color = new Color(0x00ffff);
        console.log(
          this.vrInputSource[0].handedness === "right"
            ? "主手柄为右手柄"
            : "主手柄为左手柄"
        );
      }

      this.controller1._joystickBtnState = {
        leftPressed: false,
        rightPressed: false,
        upPressed: false,
        downPressed: false,
      };

      if (this.world.getSystem(UIHandRaySystem)) {
        this.world.getSystem(UIHandRaySystem).addController(this.controller1);
      }
      if (this.world.getSystem(ObjectHandRaySystem)) {
        this.world
          .getSystem(ObjectHandRaySystem)
          .addController(this.controller1);
      }
    }

    if (
      !this.controller2 &&
      this.vrInputSource.length >= 2 &&
      this.vrInputSource[1].profiles.length > 0
    ) {
      this.controller2 = this.renderer.xr.getController(1);
      this.controller2.visible = true; // 显式设置可见性
      this.controller2.inputSource = this.vrInputSource[1];
      this.VRCamera.add(this.controller2);
      this.vrInputSource[1]._target = { target: this.controller2 };

      let handModel;
      if (!this.isWindows) {
        // 加载pico右手柄
        if (this.vrInputSource[1].handedness === "right") {
          console.log("创建右手柄");
          if (this.deviceType === VRDeviceType.Pico4)
            handModel = this.handleModels.pico4Right;
          else handModel = this.handleModels.picoRight;
        } else {
          console.log("创建左手柄");
          if (this.deviceType === VRDeviceType.Pico4)
            handModel = this.handleModels.pico4Left;
          else handModel = this.handleModels.picoLeft;
        }
      } else {
        handModel = this.handleModels.htc.clone();
      }

      if (this.vrInputSource[1].handedness === "right") {
        this.rightController = this.controller2;
      } else {
        this.leftController = this.controller2;
      }

      this.controller2._handModel = handModel;
      this.controllerGrip2 = this.renderer.xr.getControllerGrip(1);
      this.controllerGrip2.add(handModel);
      if (!this.isWindows) {
        handModel.rotateX(-Math.PI / 4);
      }
      this.VRCamera.add(this.controllerGrip2);

      let type = "htc";
      if (this.deviceType === VRDeviceType.Pico4) type = "pico4";
      else if (this.deviceType === VRDeviceType.PicoNeo3) type = "pico3";
      this.controller2._handdleButtonControl = new VRHandleButtonControls(
        handModel,
        type,
        this.vrInputSource[1].handedness,
        this
      );

      if (!this.mainController) {
        this.mainController = this.controller2;
        this.controller2._isMain = true;
        console.log(
          this.vrInputSource[1].handedness === "right"
            ? "主手柄为右手柄"
            : "主手柄为左手柄"
        );
      }
      this.controller2._joystickBtnState = {
        leftPressed: false,
        rightPressed: false,
        upPressed: false,
        downPressed: false,
      };

      //line可视
      {
        let line_geometry = new BufferGeometry().setFromPoints([
          new Vector3(0, 0, 0),
          new Vector3(0, 0, -1),
        ]);
        const mat = new LineBasicMaterial({
          depthWrite: true,
          depthTest: false,
          transparent: true,
          color: this.mainController === this.controller2 ? 0x00ffff : 0xffffff,
        });
        let line = new Line(line_geometry, mat);
        line.scale.z = 1.5;
        line.renderOrder = Infinity;
        line.name = "line";
        this.controller2.add(line);
        this.controller2._line = line;
      }

      if (this.world.getSystem(UIHandRaySystem)) {
        this.world.getSystem(UIHandRaySystem).addController(this.controller2);
      }
      if (this.world.getSystem(ObjectHandRaySystem)) {
        this.world
          .getSystem(ObjectHandRaySystem)
          .addController(this.controller2);
      }
    }

    this._addFloorCursor();
    this._addCursor();

    this.manager.eventManager.addEventListener(
      EventManagerCommand.vrFloorCursorLoaded,
      () => {
        const waitInitEcsy = setInterval(() => {
          clearInterval(waitInitEcsy);

          if (this.mainController) {
            if (!this.world.getSystem(UIHandRaySystem)) {
              this.world.registerSystem(UIHandRaySystem, {
                controller1: this.controller1,
                controller2: this.controller2,
              });
            }
            if (!this.world.getSystem(ObjectHandRaySystem))
              this.world.registerSystem(ObjectHandRaySystem, {
                controller1: this.controller1,
                controller2: this.controller2,
              });

            console.log("Ecsy初始化完成");
          }
        }, 200);

        this.manager.eventManager.removeEventListeners(
          EventManagerCommand.vrFloorCursorLoaded
        );
      }
    );
  }

  /**
   * 扳机键按下
   * @param {*} event
   */
  _onSelectStart(event) {
    if (!event || !event.target) return;
    event.target._triggerBtn = true;

    if (event.target._handdleButtonControl) {
      event.target._handdleButtonControl.triggerDown();
    }

    // 地形编辑模式下，触发地形修改
    if (this.manager.interface.clickMode === ClickMode.Terrain) {
      console.log("地形");
      this.mainPanel.editPanel.terrainPanel.onTriggerDown(event.target);
      // 查找地形编辑面板
      //if (this.terrainPanel) {
      //  this.terrainPanel.onTriggerDown(event.target);
      //}
    }
    if(this.manager.interface.clickMode === ClickMode.Way){
      this.mainPanel.editPanel.wayPanel.onTriggerDown(event.target);
    }

    if (!event.target._isMain) {
      return;
    }

    // 如果手柄抓取了物体，就不再检测点击事件
    if (this.handleGrabModelCount > 0) return;

    if (this.uiLib) this.uiLib.onSelectStart(event.target);
  }

  /**
   * 扳机键松开
   * @param {*} event
   */
  _onSelectEnd(event) {
    if (!event || !event.target) return;
    event.target._triggerBtn = false;

    if (event.target._handdleButtonControl) {
      event.target._handdleButtonControl.triggerUp();
    }

    // 地形编辑模式下，停止地形修改
    if (this.manager.interface.clickMode === ClickMode.Terrain) {
      console.log("地形停止");
      this.mainPanel.editPanel.terrainPanel.onTriggerUp(event.target);
      // 查找地形编辑面板
      //if (this.terrainPanel) {
      //  this.terrainPanel.onTriggerUp(event.target);
      //}
    }

    if (!event.target._isMain) {
      // 切换主副控制器
      this._changeMainController(event.target);
      return;
    }

    // 如果手柄抓取了物体，就不再检测点击事件
    //if (event.target._handModel && event.target._handModel._currentGrabObj)
    if (this.handleGrabModelCount > 0) return;

    // UI交互
    if (event.target._hoverType === "UI") {
      if (this.uiLib) this.uiLib.onSelectEnd(event.target);
    }
    // 模型交互
    else {
      // 主控制器才能与场景交互，副控制器只能位移
      if (
        this.manager.interface.clickMode !== ClickMode.None &&
        !event.target._isMain
      )
        return;

      // 点击模型
      let objs = [];
      // vr设置中位置的设定
      if (this.manager.interface.clickMode === ClickMode.VR_Position) {
        if (this.manager.vrSettingManager.cloneModels != null) {
          objs = objs.concat(this.manager.vrSettingManager.cloneModels);
        }
      }
      // 测量或模型库
      else if (
        this.manager.interface.clickMode === ClickMode.Measure ||
        this.manager.interface.clickMode === ClickMode.Model ||
        this.manager.interface.clickMode === ClickMode.None
      ) {
        if (this.movablePlane) objs.push(this.movablePlane);
        else objs.push(this.manager.sceneManager.floor);
        if (this.manager.sceneManager.paperPlane) {
          objs.push(this.manager.sceneManager.paperPlane);
        }
      }

      if (this.manager.interface.clickMode !== ClickMode.None) {
        objs = objs.concat(this.manager.sceneManager.singleModels);
      } else {
        objs = objs.concat(this.manager.vrSettingManager.planeNodes);
      }
      //console.log(objs);
      var intersects = this._getIntersections(event.target, objs, true);
      intersects = this._filtrateClick(intersects);
      this._processClick(intersects);
    }
  }

  /**
   * 抓取键按下
   * @param {*} event
   */
  _onSqueezeStart(event) {
    if (!event || !event.target) return;
    event.target._squeezeBtn = true;

    if (event.target._handdleButtonControl) {
      event.target._handdleButtonControl.gripDown();
    }

    if (!event.target._isMain) {
      return;
    }
    console.log("抓取键按下", event);
    this._grabObj(event.target);
  }

  /**
   * 抓取键松开
   * @param {*} event
   */
  _onSqueezeEnd(event) {
    if (!event || !event.target) return;
    console.log("抓取键松开", event);
    event.target._squeezeBtn = false;

    if (event.target._handdleButtonControl) {
      event.target._handdleButtonControl.gripUp();
    }

    if (!event.target._isMain) {
      // 切换主副控制器
      this._changeMainController(event.target);
      return;
    }

    this._releaseObj(event.target);
  }

  /**
   * 切换主控制器
   * @param {*} controller
   * @returns
   */
  _changeMainController(controller) {
    if (!controller) return;
    if (this.mainController === controller) return;

    if (this.mainController) {
      // 主控制器正在抓取物体，不能切换
      if (this.mainController._currentGrabObj) return;
      // 正在拖拽ui，不能切换
      if (this.uiLib && this.uiLib.dragController) {
        return;
      }
      // 正在放置模型，不能切换
      if (this.manager.interface.currentOperateModel) {
        return;
      }

      this.mainController._isMain = false;
      this.mainController._line.material.color = new Color(0xffffff);

      // 取消当前ui的选中效果
      if (
        this.mainController.lastUI &&
        this.mainController.lastUI.onEcsyHoverExit
      ) {
        this.mainController.lastUI.onEcsyHoverExit(this.mainController.lastUI);
      }
    }

    this.mainController = controller;
    this.mainController._isMain = true;
    this.mainController._line.material.color = new Color(0x00ffff);
    // 增加当前ui的选中效果
    if (
      this.mainController.lastUI &&
      this.mainController.lastUI.onEcsyHoverEnter
    ) {
      this.mainController.lastUI.onEcsyHoverEnter(this.mainController.lastUI);
    }
  }

  /**
   * 摇杆左键按下
   * @param {*} controller 控制器
   */
  _onLeftBtnDown(controller, value) {
    if (!controller) return;
  }

  /**
   * 摇杆左键松开
   * @param {*} controller 控制器
   */
  _onLeftBtnUp(controller, value) {
    if (!controller) return;
  }

  /**
   * 摇杆右键按下
   * @param {*} controller 控制器
   */
  _onRightBtnDown(controller, value) {
    if (!controller) return;
  }

  /**
   * 摇杆右键松开
   * @param {*} controller 控制器
   */
  _onRightBtnUp(controller, value) {
    if (!controller) return;
  }

  /**
   * 摇杆上键按下
   * @param {*} controller 控制器
   */
  _onUpBtnDown(controller, value) {
    if (!controller) return;
  }

  /**
   * 摇杆上键松开
   * @param {*} controller 控制器
   */
  _onUpBtnUp(controller, value) {
    if (!controller) return;
  }

  /**
   * 摇杆下键按下
   * @param {*} controller 控制器
   */
  _onDownBtnDown(controller, value) {
    if (!controller) return;
  }

  /**
   * 摇杆下键松开
   * @param {*} controller 控制器
   */
  _onDownBtnUp(controller, value) {
    if (!controller) return;
  }

  /**
   * A键或X键按下（PICO等一体机）
   * @param {*} controller 控制器
   */
  _onBtnADown(controller) {
    if (!controller) return;
    if (controller._handdleButtonControl) {
      controller._handdleButtonControl.axDown();
    }
  }

  /**
   * A键或X键松开（PICO等一体机）
   * @param {*} controller 控制器
   */
  _onBtnAUp(controller) {
    if (!controller) return;
    if (controller._handdleButtonControl) {
      controller._handdleButtonControl.axUp();
    }
  }

  /**
   * B键或Y键按下（PICO等一体机）
   * @param {*} controller 控制器
   */
  _onBtnBDown(controller) {
    if (!controller) return;
    if (controller._handdleButtonControl) {
      controller._handdleButtonControl.byDown();
    }
  }

  /**
   * B键或Y键松开（PICO等一体机）
   * @param {*} controller 控制器
   */
  _onBtnBUp(controller) {
    if (!controller) return;
    if (controller._handdleButtonControl) {
      controller._handdleButtonControl.byUp();
    }
  }

  /**
   * 实时检测光标移动过程，很耗性能
   * 这里只用于主控制器
   */
  _onCurveMove() {
    if (!this.mainController) return;

    // 只检测模型库中
    if (
      this.manager.interface.clickMode !== ClickMode.Model &&
      this.manager.interface.clickMode !== ClickMode.CreateObject
    )
      return;

    const targetObj = this.manager.interface.currentOperateModel;

    if (
      !targetObj &&
      ((this.manager.interface.clickMode === ClickMode.Model &&
        this.manager.interface.modelLibArgs.modelLibType === "matrix") ||
        this.manager.interface.clickMode === ClickMode.CreateObject)
    )
      return;

    // 光标悬停到UI上时把模型隐藏
    if (this.mainController._hoverType === "UI" && targetObj) {
      targetObj.position.y = -10000;
      return;
    }

    let dataObject = [this.manager.sceneManager.floor];
    const singleModels = this.manager.sceneManager.singleModels.filter(
      (element) => !element._delete && element.visible
    );
    dataObject = dataObject.concat(singleModels);
    var intersects = this._getIntersections(
      this.mainController,
      dataObject,
      true
    );

    if (this.manager.interface.clickMode === ClickMode.Model) {
      // 曲线编辑模式
      if (this.manager.interface.modelLibArgs.modelLibType === "curve") {
        if (intersects && intersects.length > 0) {
          if (
            this.manager.interface.curveUtil &&
            this.manager.interface.curveUtil.enabled
          ) {
            this.manager.interface.curveUtil.onPointerMove(
              intersects[0].point.clone()
            );
          }
        }
      }
      // 矩阵编辑模式
      else {
        if (intersects && intersects.length > 0) {
          let target = intersects[0];
          let point = target.point.clone();
          // 暂时先将模型放置在点击点的位置
          if (targetObj._offset) {
            targetObj.position.copy(point.add(targetObj._offset));
          } else targetObj.position.copy(point);
        }
        // 没有点击到模型
        else {
          const point = this._getContorllerDirDis(
            this.mainController,
            this.manager.interface.dis2Camera
          );
          if (targetObj._offset) {
            targetObj.position.copy(point.add(targetObj._offset));
          } else {
            targetObj.position.copy(src);
          }
        }
      }
    } else if (this.manager.interface.clickMode === ClickMode.CreateObject) {
      if (intersects && intersects.length > 0) {
        let target = intersects[0];
        let point = target.point.clone();
        // 暂时先将模型放置在点击点的位置
        if (targetObj._offset) {
          targetObj.position.copy(point.add(targetObj._offset));
        } else targetObj.position.copy(point);
      }
      // 没有点击到模型
      else {
        const point = this._getContorllerDirDis(
          this.mainController,
          this.manager.interface.dis2Camera
        );
        if (targetObj._offset) {
          targetObj.position.copy(point.add(targetObj._offset));
        } else {
          targetObj.position.copy(src);
        }
      }
    }
  }

  _addCursor() {
    if (!this.cursor) {
      let geo = new TorusGeometry(0.01, 0.005, 10, 24);
      let mat = new MeshBasicMaterial({
        color: 0x00ffff,
        depthTest: false,
        transparent: true,
      });

      this.cursor = new Mesh(geo, mat);
      this.cursor._parent = this.scene;
      this.cursor.renderOrder = Infinity;

      this.cursor2 = new Mesh(geo, mat);
      this.cursor2._parent = this.scene;
      this.cursor2.renderOrder = Infinity;

      console.log("光标加载完成");
    }

    if (this.controller1 && !this.controller1._cursor) {
      this.controller1._cursor = this.cursor;
      console.log("设置手柄1光标");
    }
    if (this.controller2 && !this.controller2._cursor) {
      this.controller2._cursor = this.cursor2;
      console.log("设置手柄2光标");
    }
  }

  async _addFloorCursor() {
    if (!this.loadFloorTexture) {
      this.loadFloorTexture = true;
      let tex = await this.manager.loadManager.loadTexture(
        "/assets/imgs/displacement.png"
      );
      let geo = new PlaneGeometry(1, 1);
      let mat = new MeshBasicMaterial({
        map: tex,
        transparent: true,
        side: DoubleSide,
        depthTest: false,
      });
      this.floorCursor = new Mesh(geo, mat);
      this.floorCursor._parent = this.scene;

      this.floorCursor2 = new Mesh(geo, mat);
      this.floorCursor2._parent = this.scene;

      this.manager.eventManager.dispach(
        EventManagerCommand.vrFloorCursorLoaded
      );

      console.log("地面图标加载完成");
    }

    if (this.controller1 && !this.controller1._floorCursor) {
      this.controller1._floorCursor = this.floorCursor;
      console.log("设置手柄1光标");
    }
    if (this.controller2 && !this.controller2._floorCursor) {
      this.controller2._floorCursor = this.floorCursor2;
      console.log("设置手柄2光标");
    }
  }

  /**
   * 主动触发手柄震动
   * @param {number} intensity 震动强度 (0.0 - 1.0)
   * @param {number} duration 持续时间（毫秒）
   * @param {string} handedness 手柄类型 'left'/'right'（可选）
   */
  async triggerHapticPulse(controller, intensity = 0.5, duration = 100) {
    const inputSource = controller.inputSource;

    if (!inputSource?.hapticActuators?.length) {
      this.makeSystemTip(JSON.stringify(inputSource.hapticActuators), 5000000);
      return;
    }

    this.makeSystemTip(inputSource.hapticActuators);
    const actuator = inputSource.hapticActuators[0];
    if (actuator.type === "vibration") {
      try {
        await actuator.pulse(intensity, duration);
      } catch (error) {
        this.makeSystemTip("Haptic feedback failed:" + error);
      }
    }
  }

  /**
   * 测试手柄能否震动
   */
  _handlePulse(controller) {
    if (!this.hapticConfig) {
      // 在类属性中添加震动配置
      this.hapticConfig = {
        default: { intensity: 0.8, duration: 500 },
        pico: { intensity: 0.6, duration: 200 },
        htc: { intensity: 1.0, duration: 1000 },
      };
    }
    this.triggerHapticPulse(controller);
  }

  /**
   * 处理手柄事件
   */
  _handleEvent() {
    if (this.vrInputSource && this.vrInputSource.length > 0) {
      this.vrInputSource.forEach((xrInputSource) => {
        if (xrInputSource.gamepad) {
          // THC
          if (this.isWindows) {
            // htc圆盘键按下时除了需要检测axes外还需要检测第三个按钮是否按下（第一个按钮是扳机键，第二个是抓取键）
            if (xrInputSource.gamepad.buttons.length !== 3) {
              console.log("不是HTC设备！");
            } else {
              if (!xrInputSource.buttonStates) {
                xrInputSource.buttonStates = {};
              }

              if (xrInputSource.gamepad.buttons[2].pressed) {
                this._updateJoystick(
                  xrInputSource.gamepad.axes[0],
                  xrInputSource.gamepad.axes[1],
                  xrInputSource
                );
              }

              if (
                xrInputSource.gamepad.buttons[0].pressed &&
                !xrInputSource.buttonStates["0"]
              ) {
                this._onSelectStart(xrInputSource._target);
              } else if (
                !xrInputSource.gamepad.buttons[0].pressed &&
                xrInputSource.buttonStates["0"]
              ) {
                this._onSelectEnd(xrInputSource._target);
              }
              if (
                xrInputSource.gamepad.buttons[1].pressed &&
                !xrInputSource.buttonStates["1"]
              ) {
                this._onSqueezeStart(xrInputSource._target);
              } else if (
                !xrInputSource.gamepad.buttons[1].pressed &&
                xrInputSource.buttonStates["1"]
              ) {
                this._onSqueezeEnd(xrInputSource._target);
              }

              xrInputSource.buttonStates["0"] =
                xrInputSource.gamepad.buttons[0].pressed;
              xrInputSource.buttonStates["1"] =
                xrInputSource.gamepad.buttons[1].pressed;
            }
          }
          // pico 或其他3Dof设备（只测试了pico ）
          else {
            if (!xrInputSource.buttonStates) {
              xrInputSource.buttonStates = {};
            }

            this._updateJoystick(
              xrInputSource.gamepad.axes[2],
              xrInputSource.gamepad.axes[3],
              xrInputSource
            );

            // this.makeSystemTip(xrInputSource.gamepad.buttons[0].pressed, 1000)
            // pico 的按钮有6个，第一二个是扳机键和抓取键，第五六个是A、B键，中间两个是菜单键，已经被设备托管了，不能使用
            if (xrInputSource.gamepad.buttons.length === 6) {
              if (
                xrInputSource.gamepad.buttons[0].pressed &&
                !xrInputSource.buttonStates["0"]
              ) {
                this._onSelectStart(xrInputSource._target);
              } else if (
                !xrInputSource.gamepad.buttons[0].pressed &&
                xrInputSource.buttonStates["0"]
              ) {
                this._onSelectEnd(xrInputSource._target);
              }
              if (
                xrInputSource.gamepad.buttons[1].pressed &&
                !xrInputSource.buttonStates["1"]
              ) {
                this._onSqueezeStart(xrInputSource._target);
              } else if (
                !xrInputSource.gamepad.buttons[1].pressed &&
                xrInputSource.buttonStates["1"]
              ) {
                this._onSqueezeEnd(xrInputSource._target);
              }
              if (
                xrInputSource.gamepad.buttons[4].pressed &&
                !xrInputSource.buttonStates["4"]
              ) {
                this._onBtnADown(xrInputSource._target.target);
              } else if (
                !xrInputSource.gamepad.buttons[4].pressed &&
                xrInputSource.buttonStates["4"]
              ) {
                this._onBtnAUp(xrInputSource._target.target);
              }
              if (
                xrInputSource.gamepad.buttons[5].pressed &&
                !xrInputSource.buttonStates["5"]
              ) {
                this._onBtnBDown(xrInputSource._target.target);
              } else if (
                !xrInputSource.gamepad.buttons[5].pressed &&
                xrInputSource.buttonStates["5"]
              ) {
                this._onBtnBUp(xrInputSource._target.target);
              }
              xrInputSource.buttonStates["0"] =
                xrInputSource.gamepad.buttons[0].pressed;
              xrInputSource.buttonStates["1"] =
                xrInputSource.gamepad.buttons[1].pressed;
              xrInputSource.buttonStates["4"] =
                xrInputSource.gamepad.buttons[4].pressed;
              xrInputSource.buttonStates["5"] =
                xrInputSource.gamepad.buttons[5].pressed;
            }
            // 用于在web端的谷歌浏览器模拟Quest pro
            else if (xrInputSource.gamepad.buttons.length === 7) {
              if (
                xrInputSource.gamepad.buttons[0].pressed &&
                !xrInputSource.buttonStates["0"]
              ) {
                this._onSelectStart(xrInputSource._target);
              } else if (
                !xrInputSource.gamepad.buttons[0].pressed &&
                xrInputSource.buttonStates["0"]
              ) {
                this._onSelectEnd(xrInputSource._target);
              }
              if (
                xrInputSource.gamepad.buttons[1].pressed &&
                !xrInputSource.buttonStates["1"]
              ) {
                this._onSqueezeStart(xrInputSource._target);
              } else if (
                !xrInputSource.gamepad.buttons[1].pressed &&
                xrInputSource.buttonStates["1"]
              ) {
                this._onSqueezeEnd(xrInputSource._target);
              }
              if (
                xrInputSource.gamepad.buttons[4].pressed &&
                !xrInputSource.buttonStates["5"]
              ) {
                this._onBtnADown(xrInputSource._target.target);
              } else if (
                !xrInputSource.gamepad.buttons[4].pressed &&
                xrInputSource.buttonStates["5"]
              ) {
                this._onBtnAUp(xrInputSource._target.target);
              }
              if (
                xrInputSource.gamepad.buttons[5].pressed &&
                !xrInputSource.buttonStates["6"]
              ) {
                this._onBtnBDown(xrInputSource._target.target);
              } else if (
                !xrInputSource.gamepad.buttons[5].pressed &&
                xrInputSource.buttonStates["6"]
              ) {
                this._onBtnBUp(xrInputSource._target.target);
              }
              xrInputSource.buttonStates["0"] =
                xrInputSource.gamepad.buttons[0].pressed;
              xrInputSource.buttonStates["1"] =
                xrInputSource.gamepad.buttons[1].pressed;
              xrInputSource.buttonStates["5"] =
                xrInputSource.gamepad.buttons[4].pressed;
              xrInputSource.buttonStates["6"] =
                xrInputSource.gamepad.buttons[5].pressed;
            }
          }
        }
      });
    }
  }

  /**
   * 更新摇杆数值
   * @param {*} horizontal 水平方向  [-1,1]
   * @param {*} vertical 垂直方向 [-1,1] 注意，垂直方向负值为上(PICO已测试，htc未测试)
   */
  _updateJoystick(horizontal, vertical, xrInputSource) {
    if (
      !xrInputSource ||
      !xrInputSource._target ||
      !xrInputSource._target.target
    )
      return;

    const controller = xrInputSource._target.target;

    if (controller._handdleButtonControl) {
      controller._handdleButtonControl.updateTouchpad(horizontal, vertical);
    }

    // 如果主控制器抓取了物体，则方向键控制抓取物体的旋转、移动和缩放
    if (this.mainController && this.mainController._handModel) {
      const handleModel = this.mainController._handModel;
      if (handleModel._currentGrabObj) {
        this.updateGirdModelState(horizontal, vertical, controller);
        return;
      }
    }

    // 摇杆按键触发事件
    if (controller._joystickBtnState) {
      // 左键按下
      if (horizontal < -0.01 && !controller._joystickBtnState.leftPressed) {
        controller._joystickBtnState.leftPressed = true;
        this._onLeftBtnDown(controller, horizontal);
      }
      // 左键抬起
      else if (horizontal > -0.01 && controller._joystickBtnState.leftPressed) {
        controller._joystickBtnState.leftPressed = false;
        this._onLeftBtnUp(controller, horizontal);
      }
      // 右键按下
      if (horizontal > 0.01 && !controller._joystickBtnState.rightPressed) {
        controller._joystickBtnState.rightPressed = true;
        this._onRightBtnDown(controller, horizontal);
      }
      // 右键抬起
      else if (horizontal < 0.01 && controller._joystickBtnState.rightPressed) {
        controller._joystickBtnState.rightPressed = false;
        this._onRightBtnUp(controller, horizontal);
      }
      // 上键按下
      if (vertical < -0.01 && !controller._joystickBtnState.upPressed) {
        controller._joystickBtnState.upPressed = true;
        this._onUpBtnDown(controller, vertical);
      }
      // 上键抬起
      else if (vertical > -0.01 && controller._joystickBtnState.upPressed) {
        controller._joystickBtnState.upPressed = false;
        this._onUpBtnUp(controller, vertical);
      }
      // 下键按下
      if (vertical > 0.01 && !controller._joystickBtnState.downPressed) {
        controller._joystickBtnState.downPressed = true;
        this._onDownBtnDown(controller, vertical);
      }
      // 下键抬起
      else if (vertical < 0.01 && controller._joystickBtnState.downPressed) {
        controller._joystickBtnState.downPressed = false;
        this._onDownBtnUp(controller, vertical);
      }
    }

    // 如果当前手柄射线指向了UI，不进行旋转移动
    if (controller._hoverType === "UI") {
      if (controller._isMain && this.uiLib) {
        this.uiLib.updateVerticleValue(vertical);
        this.uiLib.updateHorizontalValue(horizontal);
      }

      return;
    }

    this._moveCameraByJoystick(horizontal, vertical, controller);
    this._rotateCameraByJoystick(horizontal, vertical, controller);
  }

  /**
   * 修改当前手柄抓取的物体状态，右手柄方向键控制移动，左手柄方向键控制旋转和缩放
   * @param {*} horizontal
   * @param {*} vertical
   * @param {*} controller
   */
  updateGirdModelState(horizontal, vertical, controller) {
    const handleModel = this.mainController._handModel;
    const obj = handleModel._currentGrabObj;

    if (horizontal === 0 && vertical === 0) return;

    // 右手柄缩放
    if (this.rightController && this.rightController === controller) {
      // 缩放
      let speed = Math.max(
        0.1,
        1 + horizontal * this.scaleModelSpeed * this.delta
      );
      obj.scale.multiplyScalar(speed);
    }
    //  左手柄旋转和移动（沿着射线方向）
    if (this.leftController && this.leftController === controller) {
      // 旋转
      obj.rotateY(horizontal * this.rotateModelSpeed * this.delta);

      //  沿射线方向移动
      if (vertical !== 0) {
        // 获取控制器的射线方向
        const direction = new Vector3(0, 0, -1)
          .applyQuaternion(controller.quaternion)
          .normalize();

        // 计算移动距离（垂直轴控制前后）
        const moveDistance = -vertical * this.moveSpeed * this.delta;

        // 应用位置变化
        obj.position.addScaledVector(direction, moveDistance);
      }
    }

    if (this.mainPanel) this.mainPanel.updateModelValue();
  }

  /**
   * 通过摇杆旋转相机
   * @param {*} horizontal 水平方向  [-1,1]
   * @param {*} vertical 垂直方向 [-1,1] 注意，垂直方向负值为上
   * @param {*} controller 控制器
   */
  _rotateCameraByJoystick(horizontal, vertical, controller) {
    // 右手柄才旋转相机
    if (!this.rightController || controller !== this.rightController) {
      return;
    }

    if (horizontal === 0) return;

    if (this.VRCamera) {
      this.VRCamera.rotateY(-horizontal * this.rotateSpeed * this.delta);
    }
  }

  /**
   * 通过摇杆移动相机
   * @param {*} horizontal 水平方向  [-1,1]
   * @param {*} vertical 垂直方向 [-1,1] 注意，垂直方向负值为上
   * @param {*} controller 控制器
   */
  _moveCameraByJoystick(horizontal, vertical, controller) {
    // 左手柄才移动相机
    if (!this.leftController || controller !== this.leftController) {
      return;
    }

    if (horizontal === 0 && vertical === 0) return;

    // 移动相机
    if (this.VRCamera) {
      // 获取相机正前方方向（水平面投影）
      const cameraDirection = new Vector3();
      this.camera.getWorldDirection(cameraDirection);
      cameraDirection.y = 0; // 保持水平移动
      cameraDirection.normalize();
      // 计算右方向向量（用于水平移动）
      const rightDirection = new Vector3()
        .crossVectors(new Vector3(0, 1, 0), cameraDirection)
        .normalize();
      // 合成移动向量
      const moveDelta = new Vector3()
        .addScaledVector(
          cameraDirection,
          -vertical * this.moveSpeed * this.delta
        )
        .addScaledVector(
          rightDirection,
          -horizontal * this.moveSpeed * this.delta
        );

      // 检查是否超出边界
      if (this.rangeBox) {
        const newPosition = this.VRCamera.position.clone().add(moveDelta);
        if (!this.rangeBox.containsPoint(newPosition)) return;
      }

      this.VRCamera.position.add(moveDelta);

      // 射线检测地面
      if (horizontal !== 0 || vertical !== 0) {
        if (this.currentFloors && this.currentFloors.length > 0) {
          let src = this.VRCamera.position.clone().add(new Vector3(0, 0.5, 0)); // 0.5m 的台阶能上去
          let dir = new Vector3(0, -1, 0);
          if (!this.floorRay) this.floorRay = new Raycaster(src, dir, 0, 1000);
          else {
            this.floorRay.set(src, dir);
          }
          let intersects = this.floorRay.intersectObjects(
            this.currentFloors,
            false
          );
          if (intersects && intersects.length > 0) {
            this.VRCamera.position.y = intersects[0].point.y;
          }
          // 未检测到地面则重置到初始位置
          else {
            this.VRCamera.position.copy(this.srcPos);
          }
        }
      }
    }
  }

  update(dt) {
    super.update(dt);

    this.delta = dt;

    this._handleEvent();

    this._onCurveMove();

    if (this.world) {
      const elapsedTime = this.clock.elapsedTime;
      this.world.execute(dt, elapsedTime);
    }

    if (this.uiLib) {
      this.uiLib.update(dt);
    }
  }

  clear() {
    if (this.saveTextureMap) this.saveTextureMap.clear();

    this.world.unregisterSystem(UIHandRaySystem);
    this.world.unregisterSystem(ObjectHandRaySystem);

    this.rangeBox = null;

    if (this.uiLib) this.uiLib.clear();

    super.clear();
  }

  makeSystemTip(text, time = 3000) {
    if (this.systemTipPanel) {
      this.systemTipPanel.show(text, time);
    }
  }

  /**
   * 设置VR相机位置
   */
  setPlayerPosition(pos) {
    if (this.VRCamera) {
      this.VRCamera.position.set(pos.x, pos.y, pos.z);
    }
  }

  /**
   * 根据控制器进行射线检测
   * @param {*} controller
   * @param {*} objs
   * @returns
   */
  _getIntersections(controller, objs, recursive = false) {
    if (this.raycaster.setFromXRController) {
      this.raycaster.setFromXRController(controller);
    } else {
      controller.updateMatrixWorld();
      //矩阵
      this.tempMatrix
        .identity()
        .extractRotation(controller.matrixWorld.clone());
      this.raycaster.ray.origin.setFromMatrixPosition(
        controller.matrixWorld.clone()
      );
      this.raycaster.ray.direction.set(0, 0, -1).applyMatrix4(this.tempMatrix);
    }

    // 剔除空对象
    for (var i = 0; i < objs.length; i++) {
      if (!objs[i]) {
        objs.splice(i, 1);
        i--;
      }
    }

    let res = this.raycaster.intersectObjects(objs, recursive);

    return res;
  }

  /**
   * 获取控制器射线方向上指定距离的位置
   * @param {*} controller
   * @param {*} dis
   */
  _getContorllerDirDis(controller, dis) {
    this.tempMatrix.identity().extractRotation(controller.matrixWorld.clone());
    const dir = new Vector3(0, 0, -1).applyMatrix4(this.tempMatrix);
    const pos = controller
      .getWorldPosition(new Vector3())
      .add(dir.multiplyScalar(dis));
    return pos;
  }

  /**
   * 筛选鼠标点击到的物体，如排除标记点
   */
  _filtrateClick(intersects) {
    if (!intersects || intersects.length <= 0) return null;
    //return intersects[0]
    let res = null;
    for (var i = 0; i < intersects.length; i++) {
      if (
        intersects[i].object.type !== "LineSegments" &&
        intersects[i].object.visible === true &&
        intersects[i].object.material.visible === true
      ) {
        res = intersects[i];
        break;
      }
    }
    return res;
  }

  _processClick(intersects) {
    //console.log(intersects, this.manager.interface.clickMode);
    if (intersects) {
      switch (this.manager.interface.clickMode) {
        case ClickMode.Measure: // 测量
          this.manager.interface.measureUtil.clickMesh(
            intersects.point.clone()
          );
          break;
        case ClickMode.VR_Plane: // 地板设定
          this.manager.vrSettingManager.editorPlane("set", intersects.object);
          break;
        case ClickMode.VR_Position: // 位置设定
          this.manager.vrSettingManager.editorPostion("set", intersects);
          break;
        case ClickMode.SceneModel: // 场景模型
          if (intersects.object && intersects.object._root) {
            this.manager.sceneManager.attach(intersects.object._root);
            this.selectModel();
          }
          break;
        case ClickMode.Terrain: // 地形
          // TODO 功能下期开发
          break;
        case ClickMode.Material: // 材质库 选中后将其材质闪烁，可以直接修改选中的材质
          this.manager.materialManager.setSelectMat(intersects.object.material);
          // 更新VR端UI
          if (this.mainPanel) {
            this.mainPanel.setSelectMaterial(intersects.object.material);
          }
          break;
        case ClickMode.Model: // 模型库 不能选中场景中的模型，但可以在加载模型库模型后拖拽滑动在场景模型表面
          this.manager.interface.onModellibLeftClick(intersects.point);
          break;
        case ClickMode.Way: // 道路
          // TODO 功能下期开发
          break;
        case ClickMode.CreateObject:
          this.manager.interface.onCreateObjectLeftClick();
          break;
        default:
          // 位移
          if (intersects.object && intersects.object._meshtag) {
            switch (intersects.object._meshtag) {
              case "floorCollider": {
                // 位移
                if (this.VRCamera && intersects.point) {
                  let point = intersects.point.clone();
                  this.VRCamera.position.copy(point);
                }
                break;
              }
              default:
                break;
            }
          }
          break;
      }
    }
    // 没有点击到模型
    else {
      if (this.uiLib) this.uiLib.hideKeyboard();
      switch (this.manager.interface.clickMode) {
        case ClickMode.Measure: // 测量
          break;
        case ClickMode.VR_Plane: // 地板设定
          break;
        case ClickMode.VR_Position: // 位置设定
          break;
        case ClickMode.SceneModel: // 场景模型
          // this.pointNull();
          // if (this.meshclickcb) {
          //   this.meshclickcb(null);
          // } else {
          //   this.getMeshTreeIdAndMaterialDataFromMeshId(null);
          // }
          this.manager.sceneManager.detach();

          //  更新VR端UI面板
          if (this.mainPanel) {
            this.mainPanel.setSelectModel();
          }
          this.manager.interface.clickMeshToHierarchy();
          break;
        case ClickMode.Terrain: // 地形
          // TODO 功能下期开发
          break;
        case ClickMode.Material: // 材质库 选中后将其材质闪烁，可以直接修改选中的材质
          this.manager.materialManager.setSelectMat();
          // 更新VR端UI面板
          if (this.mainPanel) {
            this.mainPanel.setSelectMaterial();
          }
          break;
        case ClickMode.Model: // 模型库 不能选中场景中的模型，但可以在加载模型库模型后拖拽滑动在场景模型表面
          this.manager.interface.onModellibLeftClick();
          break;
        case ClickMode.Way: // 道路
          // TODO 功能下期开发
          break;
        case ClickMode.CreateObject:
          this.manager.interface.onCreateObjectLeftClick();
          break;
        default:
          break;
      }
    }
  }

  selectModel() {
    // 更新VR UI
    if (this.manager.sceneManager.currentSelectParent) {
      if (this.mainPanel) {
        this.mainPanel.setSelectModel(
          this.manager.sceneManager.currentSelectParent
        );
      }

      let ids = [];
      for (
        var i = 0;
        i < this.manager.sceneManager.currentSelectParent.children.length;
        i++
      ) {
        ids.push(
          this.manager.sceneManager.currentSelectParent.children[i].nodeId
        );
      }
      this.manager.interface.clickMeshToHierarchy(ids);
    } else {
      this.mainPanel.setSelectModel();
    }
  }

  /**
   * 设置是否需要显示位移标记
   * @param {*} mode
   */
  setClickMode(mode) {
    // 非编辑交互模式下才能使用射线位移
    if (mode === ClickMode.None) {
      if (this.world.getSystem(ObjectHandRaySystem))
        this.world.getSystem(ObjectHandRaySystem).setCheckFloorState(true);

      // 去掉场景模型的光标选中
      for (var i = 0; i < this.manager.sceneManager.singleModels.length; i++) {
        this.removeModelComponent(this.manager.sceneManager.singleModels[i]);
      }
      // 保留地面
      this._setupFloor();
    } else {
      if (this.world.getSystem(ObjectHandRaySystem)) {
        this.world.getSystem(ObjectHandRaySystem).setCheckFloorState(false);
      }

      this._setupModel();
    }

    if (this.mainPanel) {
      // 只有在场景模型模式和材质库模式下才能点击结构树
      if (mode === ClickMode.SceneModel || mode === ClickMode.Model) {
        this.mainPanel.setHierarchyNodeClickable(true);
      } else {
        this.mainPanel.setHierarchyNodeClickable(false);
      }
    }
  }

  /**
   * 由于增加或删除模型导致更新结构树
   * @param {*} ids 节点的nodeId数组
   * @param {*} order "delete"/"add"
   * @param {*} parentId 父节点id，设置为null表示添加到最外层
   */
  updateHierarchy(ids, order, parentId = null) {
    //console.log(ids);
    if (!ids) ids = [];
    if (!Array.isArray(ids)) ids = [ids];
    if (this.mainPanel) {
      this.mainPanel.updateHierarchy(ids, order, parentId);
    }
  }

  /**
   * 更新测量面板数据
   * @param {*} records
   */
  updateMeasureRecords(records) {
    if (this.mainPanel) {
      this.mainPanel.updateMeasureRecords(records);
    }
  }

  /**
   * 选中结构树上的一些节点
   * @param {*} model
   */
  selectHiarerchyNodes(ids) {
    if (this.mainPanel) {
      this.mainPanel.selectHiarerchyNodes(ids);
    }
  }

  /**
   * 设置进度值
   * @param {*} value 0-100
   */
  setProgress(value) {
    // console.log(value);
    //  设置进度条
    if (this.progressBarPanel) {
      this.progressBarPanel.setValue(value);
    }
  }

  /**
   * 设置当前选中的模型
   */
  setCurrentSelectModel() {
    if (
      this.mainPanel &&
      this.manager.interface.clickMode === ClickMode.SceneModel
    ) {
      this.mainPanel.setSelectModel(
        this.manager.sceneManager.currentSelectParent
      );
    }
  }

  /**
   * 打开编辑面板
   * @param {*} subPanel 子面板 "sceneModel"/"Terrain"/"Material"/"Model"/"Way"
   */
  openEditPlane(subPanel) {
    if (this.mainPanel) {
      this.mainPanel.openEditPlane(subPanel);
    }
  }

  /**
   * 获取主控制器
   * @returns {THREE.Group} 主控制器对象
   */
  getMainController() {
    return this.mainController;
  }

  _releaseObj(controller) {
    this._releaseGrab(controller);
  }

  /**
   * 松开抓取时的物体
   * @param {*} controller
   */
  _releaseGrab(controller) {
    // 松开被抓取的物体
    let handModel = controller._handModel;
    if (handModel && handModel._currentGrabObj) {
      let obj = handModel._currentGrabObj;
      if (obj._nearReplaceObj) {
        delete obj._nearReplaceObj;
      } else {
        if (obj._grabParent) {
          let worldPos = obj.getWorldPosition(new Vector3());
          let worldQua = obj.getWorldQuaternion(new Quaternion());
          let worldScale = obj.getWorldScale(new Vector3());
          obj.parent.remove(obj);
          obj.position.copy(worldPos);
          obj.quaternion.copy(worldQua);
          obj.scale.copy(worldScale);
          obj._grabParent.attach(obj);

          this.manager.sceneManager.detach();
          this.selectModel();

          delete obj._grabParent;
        }
      }

      handModel._currentGrabObj = null;
      this.handleGrabModelCount--;

      controller.disableRay = false; // 允许光标射线检测

      if (this.handleGrabModelCount <= 0) {
        // 全部物体释放后启用UI射线光标
        if (this.world.getSystem(UIHandRaySystem))
          this.world.getSystem(UIHandRaySystem).enabled = true;

        // if (this.cursor) {
        //   this.cursor._parent.attach(this.cursor);
        // }
      }
    }
  }

  /**
   * 抓取物体
   */
  _grabObj(controller) {
    // 主控制器 模型库模式

    const targetObj = this.manager.interface.currentOperateModel;
    if (this.manager.interface.clickMode === ClickMode.Model) {
      const curveUtil = this.manager.interface.curveUtil;
      // 停止模型放置状态
      // 曲线
      if (this.manager.interface.modelLibArgs.modelLibType === "curve") {
        if (curveUtil.enabled) {
          curveUtil.stopDraw();
          if (targetObj) {
            this.manager.interface._generageModelByCurve();
          }
        } else {
          if (targetObj) {
            this.manager.materialManager.deleteNode(targetObj);
            this.manager.interface.currentOperateModel = null;
          }

          this.mainPanel.setModellibDefault();
        }
      }
      // 矩阵
      else {
        if (targetObj) {
          this.manager.materialManager.deleteNode(targetObj);
          this.manager.interface.currentOperateModel = null;
          this.mainPanel.setModellibDefault();
        }
      }
      return;
    } else if (this.manager.interface.clickMode === ClickMode.CreateObject) {
      if (targetObj) {
        this.manager.interface.currentOperateModel.removeFromParent();
        this.manager.interface.currentOperateModel = null;
      }
      return;
    }

    // 场景模型模式才能抓取模型
    if (this.manager.interface.clickMode !== ClickMode.SceneModel) return;

    // 检测当前射线是否有碰撞到物体
    let intersects = this._getIntersections(
      controller,
      this.manager.sceneManager.singleModels,
      true
    );
    intersects = this._filtrateClick(intersects);
    if (intersects) {
      // 增加一个居中父对象，方便统一操作
      if (intersects.object._root) {
        this.manager.sceneManager.attach(intersects.object._root);
      } else {
        this.manager.sceneManager.attach(intersects.object);
      }
      this.selectModel();

      this._toGrabModel(
        this.manager.sceneManager.currentSelectParent,
        controller
      );
    }
  }

  /**
   * 抓取动作
   */
  _toGrabModel(object, controller) {
    let handModel = controller._handModel;

    object._grabParent = object.parent;
    let worldPos = object.getWorldPosition(new Vector3());
    let worldQua = object.getWorldQuaternion(new Quaternion());
    let worldScale = object.getWorldScale(new Vector3());
    object.removeFromParent();
    object.position.copy(worldPos);
    object.quaternion.copy(worldQua);
    object.scale.copy(worldScale);
    handModel.attach(object);
    handModel._currentGrabObj = object;
    object._VRHandModel = handModel;

    this.handleGrabModelCount++;

    // 抓取物品后禁止UI射线光标
    if (this.world.getSystem(UIHandRaySystem))
      this.world.getSystem(UIHandRaySystem).enabled = false;

    // if (this.cursor) {
    //   handModel.attach(this.cursor);
    // }

    controller.disableRay = true; // 禁止当前手柄光标射线检测
  }

  /**
   * 使用bvh检测obj是否跟handModel碰撞
   * @param {*} obj
   * @param {*} handModel
   * @returns
   */
  _bvhShapecast(obj, handModel) {
    const collider = this._generateBVH(obj);
    handModel.updateMatrixWorld();

    const tempBox = new Box3().expandByObject(handModel);

    const size = tempBox.getSize(new Vector3());

    const targetToBvh = new Matrix4()
      .copy(collider.matrixWorld)
      .invert()
      .multiply(handModel.matrixWorld);
    var distanceResult1 = {};
    var distanceResult2 = {};
    var minDistance = 0;
    var maxDistance = size.x / 2;
    var foundPoint = !!collider.geometry.boundsTree.closestPointToGeometry(
      handModel.geometry,
      targetToBvh,
      distanceResult1,
      distanceResult2,
      minDistance,
      maxDistance
    );
    if (foundPoint) {
      //this.makeSystemTip('触碰')
      return true;
    }

    return false;
  }

  //由于bvh仅针对单个mesh，所以要先对model处理一下，把多个geometry（如果存在）合并成一个
  _generateBVH(model) {
    let geos = [];
    //model.updateMatrixWorld(true);
    if (
      model.type === "Object3D" ||
      model.type === "Group" ||
      model.type === "Scene"
    ) {
      model.traverse((node) => {
        if (node.geometry) {
          if (node.geometry.type === "Geometry") {
            let bg = new BufferGeometry().fromGeometry(node.geometry);
            bg.applyMatrix4(node.matrixWorld);
            for (let a in bg.attributes) {
              if (a !== "position") bg.deleteAttribute(a);
            }
            geos.push(bg);
          } else {
            let temp = node.geometry.clone();
            temp.applyMatrix4(node.matrixWorld);
            for (let a in temp.attributes) {
              if (a !== "position") temp.deleteAttribute(a);
            }
            geos.push(temp);
          }
        }
      });
    } else if (model.type === "Mesh") {
      if (model.geometry.type === "Geometry") {
        let bg = new BufferGeometry().fromGeometry(model.geometry);
        bg.applyMatrix4(model.matrixWorld);
        for (let a in bg.attributes) {
          if (a !== "position") bg.deleteAttribute(a);
        }
        geos.push(bg);
      } else {
        let temp = model.geometry.clone();
        temp.applyMatrix4(model.matrixWorld);
        for (let a in temp.attributes) {
          if (a !== "position") temp.deleteAttribute(a);
        }
        geos.push(temp);
      }
    }

    const mergedGeometry = mergeGeometries(geos, false);
    mergedGeometry.boundsTree = new MeshBVH(mergedGeometry, {
      lazyGeneration: false,
    });
    let c = new Color(Math.random(), Math.random(), Math.random());
    let collider = new Mesh(
      mergedGeometry,
      new MeshBasicMaterial({
        wireframe: false,
        opacity: 0.5,
        transparent: true,
        color: c,
      })
    );
    return collider;
  }

  isInVr() {
    return this.isInVR;
  }

  /**
   * 点击“ENTER VR”按钮后自动调用
   */
  _setup() {
    this.VRCamera = new Object3D();

    //const target = this.manager.sceneManager.controls.target;
    this.isInVR = true;

    this.srcPos = new Vector3();
    if (
      this.manager.vrSettingManager.VRNews &&
      this.manager.vrSettingManager.VRNews.position.length > 0
    ) {
      this.srcPos.copy(this.manager.vrSettingManager.VRNews.position[0]);
    } else {
      let box = new Box3();
      const singleModels = this.manager.sceneManager.singleModels.filter(
        (element) => !element._delete && element.visible
      );
      for (var i = 0; i < singleModels.length; i++) {
        box.expandByObject(singleModels[i]);
      }
      let center = box.getCenter(new Vector3());
      let size = box.getSize(new Vector3());

      const src = new Vector3(center.x, center.y + size.y, center.z);
      const dir = center.clone().sub(src).normalize();
      const ray = new Raycaster(src, dir);
      let objs = [];
      // if (this.manager.vrSettingManager.planeNodes)
      //   objs = objs.concat(this.manager.vrSettingManager.planeNodes);
      objs = objs.concat(singleModels);
      if (this.manager.sceneManager.floor)
        objs.push(this.manager.sceneManager.floor);
      if (objs.length > 0) {
        const intersects = ray.intersectObjects(objs, true);

        if (intersects && intersects.length > 0) {
          this.srcPos.set(
            intersects[0].point.x,
            intersects[0].point.y,
            intersects[0].point.z
          );
        }
      }
    }

    this.VRCamera.position.set(this.srcPos.x, this.srcPos.y, this.srcPos.z);
    this.camera.lookAt(new Vector3());
    this.VRCamera.add(this.camera);

    this.scene.add(this.VRCamera);

    if (this.manager.sceneManager.transformcontrol) {
      this.manager.sceneManager.transformcontrol.removeFromParent();
    }

    this.manager.sceneManager.detach();
    this.manager.materialManager.setSelectMat();

    // 创建包围区域，让相机不能超出包围区域，可移动区域范围不超过模型10米
    {
      let rangeBox = new Box3();
      const singleModels = this.manager.sceneManager.singleModels.filter(
        (element) => !element._delete && element.visible
      );
      for (var i = 0; i < singleModels.length; i++) {
        rangeBox.expandByObject(singleModels[i]);
      }
      rangeBox.min.add(new Vector3(-10, -10, -10));
      rangeBox.max.add(new Vector3(10, 10, 10));
      this.rangeBox = rangeBox;

      const center = rangeBox.getCenter(new Vector3());
      const size = rangeBox.getSize(new Vector3());

      // 创建可位移区域，用于代替默认的草地
      {
        let geo = new PlaneGeometry(1, 1);
        let mat = new MeshBasicMaterial({
          color: 0x0000ff,
          //side: DoubleSide,
          transparent: true,
          opacity: 0.0,
        });
        this.movablePlane = new Mesh(geo, mat);
        this.movablePlane.position.set(center.x, 0, center.z);
        this.movablePlane.scale.set(size.x, size.z, 1);
        this.movablePlane.name = "movablePlane";
        this.scene.add(this.movablePlane);
        this.movablePlane.lookAt(center);
        this.setPlaneComponent(this.movablePlane);
      }
    }

    //等待world初始化
    var interval = setInterval(() => {
      if (this.world !== null) {
        this._setupFloor();
        this._setupPanels();

        clearInterval(interval);
      }
    }, 100);
  }

  /**
   * 进入VR时给场景中的模型添加射线拾取标记
   */
  _setupModel() {
    for (var i = 0; i < this.manager.sceneManager.singleModels.length; i++) {
      const obj = this.manager.sceneManager.singleModels[i];
      this.setModelComponent(obj);
    }
  }

  // 设置地面
  _setupFloor() {
    this.currentFloors = [];
    if (this.movablePlane) {
      this.setPlaneComponent(this.movablePlane);
      this.currentFloors.push(this.movablePlane);
    } else if (this.manager.sceneManager && this.manager.sceneManager.floor) {
      this.setPlaneComponent(this.manager.sceneManager.floor);
      this.currentFloors.push(this.manager.sceneManager.floor);
    }
    if (this.manager.sceneManager.paperPlane) {
      this.setPlaneComponent(this.manager.sceneManager.paperPlane);
      this.currentFloors.push(this.manager.sceneManager.paperPlane);
    }
    if (this.manager.vrSettingManager.planeNodes) {
      for (
        var i = 0;
        i < this.manager.vrSettingManager.planeNodes.length;
        i++
      ) {
        let tempObj = this.manager.vrSettingManager.planeNodes[i];
        this.setPlaneComponent(tempObj);
        this.currentFloors.push(tempObj);
      }
    }
  }

  /**
   * 为模型添加射线拾取标记
   * @param {*} model
   */
  setModelComponent(model) {
    if (!model) return;

    model.traverse((node) => {
      if (node.isMesh) {
        this._addComponents(node, 5);
        if (!node._meshtag) node._meshtag = "sceneModel";
      }
    });
  }

  /**
   * 移除模型的射线拾取标记
   * @param {*} model
   * @returns
   */
  removeModelComponent(model) {
    if (!model) return;
    model.traverse((node) => {
      if (node._entity) {
        try {
          node._entity.removeAllComponents(); // Remove all components on this entity.
          node._entity.remove(); // Remove this entity from the world.

          delete node._entity;
        } catch (e) {
          //console.log(e);
        }
      }
    });
  }

  /**
   * 设置地面射线拾取标记
   * @param {*} mesh
   */
  setPlaneComponent(mesh) {
    this._addComponents(mesh, 5);
    mesh._meshtag = "floorCollider";
  }

  /**
   * 初始化各个面板
   */
  async _setupPanels() {
    this.mainPanel = new MainPanel(this.manager);
    this.systemTipPanel = new SystemTipPanel(this.manager);
    this.progressBarPanel = new ProgressBarPanel(this.manager);

    if (this.uiLib) {
    }

    this.dragSlider = false;
  }

  exitVr() {
    this.el.removeChild(this.vrBtn);
    if (this.xrSession) {
      this.xrSession.end();
      this.xrSession = null;
    }
    this.isInVR = false;
    this.manager.sceneManager.scene.add(this.camera);

    this.rangeBox = null;

    Util.disposeFun(this.VRCamera);
    this.controller1 = null;
    this.controller2 = null;
    Util.disposeFun(this.cursor);
    Util.disposeFun(this.cursor2);
    Util.disposeFun(this.floorCursor);
    Util.disposeFun(this.floorCursor2);

    this.world.unregisterSystem(UIHandRaySystem);
    this.world.unregisterSystem(ObjectHandRaySystem);

    if (this.manager.sceneManager.transformcontrol) {
      if (this.manager.sceneManager.transformScene)
        this.manager.sceneManager.transformScene.add(
          this.manager.sceneManager.transformcontrol
        );
      // 高版本不需要添加到场景中
      else
        this.manager.sceneManager.scene.add(
          this.manager.sceneManager.transformcontrol
        );
    }

    VRButton.inxrWorld = false;
    this.vrBtn = VRButton.createButton(
      this.renderer,
      this._setup.bind(this),
      this._setInputSourceList.bind(this),
      this._setXrSession.bind(this)
    );
    this.el.appendChild(this.vrBtn);
  }

  /**
   *
   * @param {*} object
   * @param {*} priority 射线检测的优先级，VR按钮最高为0，其次是背景、标签、模型、2Dplayer、地面，其中模型没设置优先级
   */
  _addComponents(object, priority = 0) {
    if (!object) return;
    if (
      this.world &&
      this.world.hasRegisteredComponent(Object3DCmp) &&
      this.world.hasRegisteredComponent(ObjectIntersectableCmp)
    ) {
      if (object.type === "Object3D" || object.type === "Group") {
        for (var i = 0; i < object.children.length; i++) {
          this._addComponents(object.children[i], priority);
        }
      } else {
        object._priority = priority;
        let entity;
        if (object._entity) {
          entity = object._entity;
        } else entity = this.world.createEntity();
        if (!entity.hasComponent(Object3DCmp))
          entity.addComponent(Object3DCmp, { object: object });
        if (!entity.hasComponent(ObjectIntersectableCmp))
          entity.addComponent(ObjectIntersectableCmp);
        object._entity = entity;
      }
    }
  }

}
